import { useState, useCallback } from 'react';
import apiService from '../services/api';
import { compressForDatabase } from '../utils/imageUtils';

// Хук для управления регистрацией водителя
export const useDriverRegistration = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [driverId, setDriverId] = useState(null);
  const [registrationStep, setRegistrationStep] = useState(0);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);

  // Сброс ошибки
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const sendVerificationCode = useCallback(async (phone) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.sendVerificationCode(phone);
      if (!response.success) {
        throw new Error(response.message || 'Не удалось отправить код');
      }
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyPhoneCode = useCallback(async (phone, code) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.verifyPhoneCode(phone, code);
      if (!response.success) {
        throw new Error(response.message || 'Код подтверждения неверный');
      }
      setPhoneVerified(true);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const sendEmailVerificationCode = useCallback(async (email) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.sendEmailVerificationCode(email);
      if (!response.success) {
        throw new Error(response.message || 'Не удалось отправить код на email');
      }
      setEmailVerified(false);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyEmailCode = useCallback(async (email, code) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.verifyEmailCode(email, code);
      if (!response.success) {
        throw new Error(response.message || 'Код подтверждения email неверный');
      }
      setEmailVerified(true);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Создание водителя
  const createDriver = useCallback(async (driverData) => {
    console.log('🔄 Hook: createDriver вызван с данными:', driverData);
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.createDriver(driverData);
      console.log('📨 Hook: Ответ от API createDriver:', response);
      
      if (response.success) {
        setDriverId(response.data.driver_id);
        setRegistrationStep(response.data.registration_step);
        console.log('✅ Hook: Driver ID установлен:', response.data.driver_id);
        return response;
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      console.error('❌ Hook: Ошибка createDriver:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Загрузка документа
  const uploadDocument = useCallback(async (documentType, imageData, filename = 'document.jpg') => {
    if (!driverId) {
      throw new Error('Водитель не создан');
    }

    setLoading(true);
    setError(null);
    
    try {
      console.log(`📤 Отправляем ${documentType}...`);
      
      // Изображение уже сжато на уровне UI
      const response = await apiService.uploadDocumentFromBase64(
        driverId, 
        documentType, 
        imageData, 
        filename
      );
      
      if (response.success) {
        setRegistrationStep(response.data.registration_step);
        return response;
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [driverId]);

  // Завершение регистрации
  const completeRegistration = useCallback(async () => {
    console.log('🏁 Hook: completeRegistration вызван для driver ID:', driverId);
    
    if (!driverId) {
      console.error('❌ Hook: Driver ID не найден!');
      throw new Error('Водитель не создан');
    }

    setLoading(true);
    setError(null);
    
    try {
      console.log('📤 Hook: Отправляем запрос completeRegistration...');
      const response = await apiService.completeRegistration(driverId);
      console.log('📨 Hook: Ответ от API completeRegistration:', response);
      
      if (response.success) {
        setRegistrationStep(8);
        console.log('✅ Hook: Регистрация завершена успешно');
        return response;
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      console.error('❌ Hook: Ошибка completeRegistration:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [driverId]);

  // Получение информации о водителе
  const getDriverInfo = useCallback(async (phone) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.getDriver(phone);
      
      if (response.success) {
        setDriverId(response.data.driver.id);
        setRegistrationStep(response.data.driver.registration_step);
        return response;
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Сброс состояния
  const resetRegistration = useCallback(() => {
    setDriverId(null);
    setRegistrationStep(0);
    setPhoneVerified(false);
    setEmailVerified(false);
    setError(null);
    setLoading(false);
  }, []);

  return {
    // Состояние
    loading,
    error,
    driverId,
    registrationStep,
    phoneVerified,
    emailVerified,
    
    // Методы
    sendVerificationCode,
    verifyPhoneCode,
    sendEmailVerificationCode,
    verifyEmailCode,
    createDriver,
    uploadDocument,
    completeRegistration,
    getDriverInfo,
    clearError,
    resetRegistration
  };
};
